import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { userAppData } from '../MainApp.js';
import { BrowserRouter as Router, useParams, useNavigate } from "react-router-dom";
import './bookingsHome.css';
import { SEC_ApiCall, SEC_DataApiPost } from '../func.js';
import { ApiError } from '../common/apiError.js';
import LoadingComp from '../common/loading.js';
import Header from '../common/header.js';
import { DailyBookingsTable, WeeklyBookingsTable } from './bookingsTables.js';
import BulkBookingModal from './bulkBookingModal.js';
import BulkCancelModal from './bulkCancelModal.js';
import EditBookingModal from './editBookingModal.js';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DatePickerComp from '../common/datePickerComp.js';
import WeekPickerComp from '../common/weekPickerComp.js';




export default function BookingsHome() {
    const [bookingsDataState, setBookingsDataState] = useState([]);
    const [isLoadedState, setIsLoadedState] = useState(false);
    const [apiErrorState, setApiErrorState] = useState(null);
    const [postApiErrorState, setPostApiErrorState] = useState(null);
    const [apiErrorTextState, setApiErrorTextState] = useState('');
    const [dateChangeIsLoadedState, setDateChangeIsLoadedState] = useState(false);
    const [refreshingSlotState, setRefreshingSlotState] = useState(null);

    const [headerTextState, setHeaderTextState] = useState('');

    const [lessonStructureState, setLessonStructureState] = useState(null);
    const [roomsDataState, setRoomsDataState] = useState(null);


    // bulk booking modal states
    const [bulkModalActiveState, setBulkModalActiveState] = useState(false);
    const [bulkQuestionsState, setBulkQuestionsState] = useState([]);
    const [bulkRoomState, setBulkRoomState] = useState(null);
    const [bulkLessonState, setBulkLessonState] = useState(null);
    const [bulkRoomNameState, setBulkRoomNameState] = useState(null);
    const [bulkDateState, setBulkDateState] = useState(null);

    // bulk cancel modal states
    const [bulkCancelActiveState, setBulkCancelActiveState] = useState(false);
    const [bulkCancelSeriesState, setBulkCancelSeriesState] = useState(null);
    const [bulkCancelClickedState, setBulkCancelClickedState] = useState(null);
    const [bulkCancelPrefetchedState, setBulkCancelPrefetchedState] = useState(null);

    // edit booking modal states
    const [editModalActiveState, setEditModalActiveState] = useState(false);
    const [editQuestionsState, setEditQuestionsState] = useState([]);
    const [editRoomState, setEditRoomState] = useState(null);
    const [editLessonState, setEditLessonState] = useState(null);
    const [editRoomNameState, setEditRoomNameState] = useState(null);
    const [editDateState, setEditDateState] = useState(null);
    const [editResponsesState, setEditResponsesState] = useState({});
    const [editProxyAllowedState, setEditProxyAllowedState] = useState(false);
    const [editProxyLockedState, setEditProxyLockedState] = useState(false);
    const [editInitialProxyUserState, setEditInitialProxyUserState] = useState(null);

    const [requiredDateState, setRequiredDateState] = useState(moment(Date()).format("DDMMyyyy"));
    const [requiredDayState, setRequiredDayState] = useState(moment(Date()).format('dddd'));
    const [requiredDateFormattedState, setDateFormattedState] = useState(moment(Date()).format("DD/MM/yyyy"));
    const [requiredDatePickerState, setRequiredDatePickerState] = useState(new Date());

    const [viewModeState, setViewModeState] = useState('day');
    const initialMonday = moment().startOf('isoWeek');
    const [requiredWeekStartState, setRequiredWeekStartState] = useState(initialMonday.format("DDMMyyyy"));
    const [requiredWeekStartPickerState, setRequiredWeekStartPickerState] = useState(initialMonday.toDate());

    // Build an array of objects representing Monday to Friday for the selected
    // week. Each entry contains the API format, day name and display format.
    const weekDatesState = useMemo(() => {
        const start = moment(requiredWeekStartPickerState);
        return Array.from({ length: 5 }, (_, i) => ({
            api: start.clone().add(i, 'days').format("DDMMyyyy"),
            name: start.clone().add(i, 'days').format('dddd'),
            display: start.clone().add(i, 'days').format("DD/MM/yyyy")
        }));
    }, [requiredWeekStartPickerState]);



    const { group } = useParams();
    let groupIdUrl = group;
    const allGroups = userAppData['bookingsMetadata']["groups"] || [];
    const groups = allGroups.filter((group) => String(group?.type || 'LESSON').toUpperCase() === 'LESSON');
    const subGroupMeta = userAppData['bookingsMetadata']["subGroups"] || [];
    let groupName = groups.find(g => g.groupId === groupIdUrl)?.name || "Unknown Group";

    // Determine the user's permission level for a specific room. Room level
    // permissions take precedence over group level permissions.
    function getRoomPermission(roomId) {
        const roomAccess = userAppData['bookingsData']?.roomAccess || [];
        const roomEntry = roomAccess.find(r => r.id === roomId);
        if (roomEntry) {
            return roomEntry.perms;
        }

        const metadataRooms = userAppData['bookingsMetadata']?.rooms || [];
        const roomMeta = metadataRooms.find(r => r.roomId === roomId);
        const groupId = roomMeta?.groupId;
        const groupAccess = userAppData['bookingsData']?.groupAccess || [];
        const groupEntry = groupAccess.find(g => g.id === groupId);
        return groupEntry ? groupEntry.perms : 'view';
    }

    // Group rooms by their sub-group based on metadata retrieved at login and
    // attach the user's permission level to each room object.
    const roomsBySubGroup = useMemo(() => {
        if (!roomsDataState) {
            return {};
        }

        const grouped = {};
        const metaRooms = userAppData['bookingsMetadata']?.rooms || [];

        // Build groups using metadata so only rooms for this group are considered
        metaRooms.forEach((metaRoom) => {
            if (metaRoom.groupId !== groupIdUrl) {
                return; // skip rooms from other groups
            }

            const roomData = roomsDataState[metaRoom.roomId];
            if (!roomData) {
                return; // booking data is missing for this room
            }

            const subId = metaRoom.subGroupId || 'noSubGroup';
            if (!grouped[subId]) {
                grouped[subId] = {};
            }

            grouped[subId][metaRoom.roomId] = {
                ...roomData,
                perm: getRoomPermission(metaRoom.roomId)
            };
        });

        return grouped;
    }, [roomsDataState, groupIdUrl]);


    async function bookingsApiCall(showGlobalLoading = true) {
        if (showGlobalLoading) {
            setDateChangeIsLoadedState(false);
        }
        let url = 'bookings/day-view';
        let params = { "group": group };
        if (viewModeState === 'week') {
            url = 'bookings/week-view';
            params['weekStart'] = requiredWeekStartState;
        } else {
            params['date'] = requiredDateState;
        }
        try {
            const response = await SEC_ApiCall(url, params, setApiErrorState);
            setBookingsDataState(response);
            setLessonStructureState(response['lessonStructure']);
            setRoomsDataState(response['rooms']);
            setIsLoadedState(true);
            setDateChangeIsLoadedState(true);
        } catch (e) {
            setIsLoadedState(true);
            setApiErrorState('error');
            setApiErrorTextState(e);
        }
    };

    useEffect(() => {
        let headerText = "Bookings - " + groupName;
        setHeaderTextState(headerText);
        bookingsApiCall();
    }, [group, requiredDateState, viewModeState, requiredWeekStartState]);


    function reload(showGlobalLoading = true) {
        let headerText = "Bookings - " + groupName;
        setHeaderTextState(headerText);
        return bookingsApiCall(showGlobalLoading);
    };

    async function handleBulkBookingComplete(result = null) {
        const slot = result?.slot;
        if (slot && slot.roomId && slot.lessonId && slot.date) {
            // For single lesson bookings, refresh data without hiding the table.
            try {
                setRefreshingSlotState({
                    roomId: slot.roomId,
                    lessonId: slot.lessonId,
                    date: slot.date,
                    previousVisual: 'free'
                });
                await reload(false);
            } finally {
                setRefreshingSlotState(null);
            }
            return;
        }

        // Bulk bookings can affect many cells, so keep the standard full refresh.
        await reload(true);
    }

    // Switch between day and week views. When moving to week view the selected
    // date is used to determine the Monday that starts the displayed week.
    function toggleView() {
        // Ensure a fresh API call is made whenever the view mode changes
        // by resetting the loaded state prior to toggling.
        setDateChangeIsLoadedState(false);
        if (viewModeState === 'day') {
            const monday = moment(requiredDatePickerState).startOf('isoWeek');
            setRequiredWeekStartState(monday.format("DDMMyyyy"));
            setRequiredWeekStartPickerState(monday.toDate());
            setViewModeState('week');
        } else {
            setViewModeState('day');
        }
    }

    function handleFreeRoomClick(roomId, lessonId, roomName, date = requiredDateState, day = requiredDayState, dateFormatted = requiredDateFormattedState) {
        const metadataRooms = userAppData['bookingsMetadata'].rooms || [];
        const roomMeta = metadataRooms.find(r => r.roomId === roomId);
        const questionsRaw = roomMeta && roomMeta.questions ? roomMeta.questions : [];
        const bulkAllowed = userAppData['bookingsData']?.bulkBookingAllowed;
        const proxyAllowed = userAppData['bookingsData']?.proxyBookAllowed;

        if ((!questionsRaw || questionsRaw.length === 0) && !bulkAllowed && !proxyAllowed) {
            bookingFunction('booking', roomId, lessonId, '[]', roomName, date, day, dateFormatted);
        } else {
            const formatted = questionsRaw.map((q, idx) => ({
                id: idx.toString(),
                question: q.questionText,
                questionType: q.questionType === 'polar' ? 'polarSwitch' : q.questionType
            }));
            setBulkQuestionsState(formatted);
            setBulkRoomState(roomId);
            setBulkLessonState(lessonId);
            setBulkRoomNameState(roomName);
            setBulkDateState(date);
            setBulkModalActiveState(true);
        }
    }

    function handleEditClick(roomId, lessonId, roomName, bookingInfo, date = requiredDateState, day = requiredDayState, dateFormatted = requiredDateFormattedState) {
        const metadataRooms = userAppData['bookingsMetadata'].rooms || [];
        const roomMeta = metadataRooms.find(r => r.roomId === roomId);
        const questionsRaw = roomMeta && roomMeta.questions ? roomMeta.questions : [];
        const formattedQuestions = questionsRaw.map((q, idx) => ({
            id: idx.toString(),
            question: q.questionText,
            questionType: q.questionType === 'polar' ? 'polarSwitch' : q.questionType
        }));
        const responses = {};
        (bookingInfo.questions || []).forEach(q => {
            responses[q.question] = q.answer;
        });
        setEditQuestionsState(formattedQuestions);
        setEditResponsesState(responses);
        setEditRoomState(roomId);
        setEditLessonState(lessonId);
        setEditRoomNameState(roomName);
        setEditDateState(date);
        const roomPerm = getRoomPermission(roomId);
        const allowProxy =
            roomPerm === 'manage' &&
            bookingInfo.origin !== 'TIMETABLED' &&
            userAppData['bookingsData']?.proxyBookAllowed;
        setEditProxyAllowedState(allowProxy);
        const isProxy = bookingInfo.origin === 'PROXY';
        setEditProxyLockedState(isProxy);
        if (isProxy && bookingInfo.userCogId) {
            setEditInitialProxyUserState({ value: bookingInfo.userCogId, label: bookingInfo.userId });
        } else {
            setEditInitialProxyUserState(null);
        }
        setEditModalActiveState(true);
    }

    // Open the bulk cancel modal for a specific occurrence
    // Returns a promise so callers can show a spinner while series data loads
    async function handleBulkCancelClick(roomId, lessonId, roomName, bookingInfo, date = requiredDateState, day = requiredDayState, dateFormatted = requiredDateFormattedState) {
        if (!bookingInfo || !bookingInfo.seriesId) return;
        try {
            console.log('[BulkCancel] Prefetch start', { seriesId: bookingInfo.seriesId, group });
            // Prefetch the series so the cell can show a spinner during load
            const params = { seriesId: bookingInfo.seriesId, group };
            const res = await SEC_ApiCall('bookings/BulkBooking', params);
            const rawItems = Array.isArray(res?.items) ? res.items : [];
            const mapped = rawItems.map(it => ({
                ...it,
                displayDate: moment(it.date, 'DDMMYYYY').format('DD/MM/YYYY'),
                displayLesson: String(it.lessonKey || '').replace(/_/g, ' '),
                selected: Boolean(it.date === date && it.lessonKey === lessonId),
                status: null
            }));
            if (mapped.length === 0) {
                console.warn('[BulkCancel] No occurrences returned for series', bookingInfo.seriesId);
                toast.error('❌ Failed to load bulk booking series', { autoClose: 4000, position: 'top-right' });
                return;
            }
            console.log('[BulkCancel] Prefetch success, items:', mapped.length);
            setBulkCancelPrefetchedState(mapped);
            setBulkCancelSeriesState(bookingInfo.seriesId);
            setBulkCancelClickedState({ roomId, lessonId, roomName, date, day, dateFormatted });
            setBulkCancelActiveState(true);
        } catch (e) {
            console.error('[BulkCancel] Prefetch failed', e);
            toast.error('❌ Failed to load bulk booking series', { autoClose: 4000, position: 'top-right' });
        }
    }

    async function bookingFunction(type, room, lessonId, questionsResponse, roomName, date = requiredDateState, day = requiredDayState, dateFormatted = requiredDateFormattedState, proxyFor = null, skipReload = false) {
        const formData = {
            type: type,
            group: group,
            date: date,
            room: room,
            lesson: lessonId,
            questionsResponse: questionsResponse
        };
        if (proxyFor) {
            formData.proxyFor = proxyFor;
        }

        const params = {};
        const url = 'bookings';

        try {
            const response = await SEC_DataApiPost(url, JSON.stringify(formData), params, setPostApiErrorState);
            if (response && response['data'] === 'success') {
                const message = type === 'booking'
                    ? `✅ Booking confirmed for room ${roomName} on ${lessonId.replace(/_/g, ' ')} ${day} ${dateFormatted}`
                    : `✅ Booking cancelled for room ${roomName} on ${lessonId.replace(/_/g, ' ')} ${day} ${dateFormatted}`;
                toast.success(message, { autoClose: 4000, position: 'top-right' });
                if (!skipReload) {
                    // Keep the table visible and show a spinner only in the slot being refreshed.
                    setRefreshingSlotState({
                        roomId: room,
                        lessonId,
                        date,
                        previousVisual: type === 'cancel' ? 'occupied' : 'free'
                    });
                    await reload(false);
                    setRefreshingSlotState(null);
                }
            } else {
                toast.error('❌ Booking request failed', { autoClose: 4000, position: 'top-right' });
                if (!skipReload) {
                    await reload(true);
                }
            }
        } catch (e) {
            console.error('Booking error', e);
            toast.error('❌ Booking request failed', { autoClose: 4000, position: 'top-right' });
            if (!skipReload) {
                await reload(true);
            }
        } finally {
            if (!skipReload) {
                setRefreshingSlotState(null);
            }
        }
    }

    async function updateBookingQuestions(responses, proxyFor = null) {
        const formData = {
            group: group,
            date: editDateState,
            room: editRoomState,
            lesson: editLessonState,
            questionsResponse: responses
        };
        if (proxyFor) {
            formData.proxyFor = proxyFor;
        }
        try {
            const resp = await SEC_DataApiPost('bookings/updateQuestions', JSON.stringify(formData), {}, setPostApiErrorState);
            if (resp && resp['data'] === 'success') {
                toast.success('✅ Booking updated', { autoClose: 4000, position: 'top-right' });
                setEditModalActiveState(false);
                reload();
            } else {
                toast.error('An error has occurred updating booking', { autoClose: 4000, position: 'top-right' });
            }
        } catch (e) {
            console.error('update failed', e);
            toast.error('An error has occurred updating booking', { autoClose: 4000, position: 'top-right' });
        }
    }

    function onDateSelectorChange(date) {
        let dateFormatted = moment(date).format("DD/MM/yyyy");
        let dateApiFormat = moment(date).format("DDMMyyyy");
        setRequiredDateState(dateApiFormat);
        setRequiredDatePickerState(date);
        setDateFormattedState(dateFormatted);
        const dow = moment(date).format('dddd');
        setRequiredDayState(dow);
    }

    // Handler for the week picker component. Stores the selected week's Monday
    // in both API and JavaScript Date formats.
    function onWeekSelectorChange(date) {
        const monday = moment(date).startOf('isoWeek');
        setRequiredWeekStartState(monday.format("DDMMyyyy"));
        setRequiredWeekStartPickerState(monday.toDate());
    }

    let navigate = useNavigate();

    const bookingsUser = userAppData['bookingsData']['perms'];

    // Determine if the API returned any rooms and if any are without sub-groups
    const hasRooms = roomsDataState && Object.keys(roomsDataState).length > 0;
    const hasNoSubGroupRooms = roomsBySubGroup.noSubGroup && Object.keys(roomsBySubGroup.noSubGroup).length > 0;

    if (bookingsUser == 0) {
        navigate("/app");
    }

    if (!isLoadedState) {
        return (
            <LoadingComp />
        )
    } else if (apiErrorState) {
        return (
            <ApiError />
        )
    } else if (isLoadedState && !apiErrorState) {

        return (
            <div className='internal_main_div bookingsMain'>

                <BulkBookingModal
                    modalActive={bulkModalActiveState}
                    setModalActive={setBulkModalActiveState}
                    groupId={group}
                    roomId={bulkRoomState}
                    lessonId={bulkLessonState}
                    roomName={bulkRoomNameState}
                    startDate={bulkDateState || requiredDateState}
                    questions={bulkQuestionsState}
                    bulkAllowed={userAppData['bookingsData']?.bulkBookingAllowed}
                    proxyAllowed={userAppData['bookingsData']?.proxyBookAllowed}
                    onComplete={handleBulkBookingComplete}
                />

                <EditBookingModal
                    modalActive={editModalActiveState}
                    setModalActive={setEditModalActiveState}
                    roomId={editRoomState}
                    lessonId={editLessonState}
                    roomName={editRoomNameState}
                    date={editDateState || requiredDateState}
                    questions={editQuestionsState}
                    initialResponses={editResponsesState}
                    proxyAllowed={editProxyAllowedState}
                    proxyLocked={editProxyLockedState}
                    initialProxyUser={editInitialProxyUserState}
                    onComplete={updateBookingQuestions}
                />

                <BulkCancelModal
                    modalActive={bulkCancelActiveState}
                    setModalActive={setBulkCancelActiveState}
                    groupId={group}
                    seriesId={bulkCancelSeriesState}
                    clicked={bulkCancelClickedState}
                    prefetchedOccurrences={bulkCancelPrefetchedState}
                    onComplete={reload}
                />



                <div className='bookingsHeaderWrap'>
                    <Header
                        text={headerTextState}
                        rightContent={
                            <button className='bookingsToggleButton' onClick={toggleView}>
                                {viewModeState === 'day' ? 'Switch to Week View' : 'Switch to Day View'}
                            </button>
                        }
                    />
                </div>

                {viewModeState === 'day' && (
                    <>
                        <div className='bookingsControlCard'>
                            <DatePickerComp date={requiredDatePickerState} onDateChange={onDateSelectorChange} day={requiredDayState} />
                        </div>

                        {!dateChangeIsLoadedState ? (
                            <LoadingComp />
                        ) : (
                            <>
                                {!hasRooms && (
                                    <div className='bookingsTableCard'>
                                        <DailyBookingsTable
                                            lessonStructure={lessonStructureState}
                                            roomsData={{}}
                                            cancelBookingFunction={bookingFunction}
                                            onFreeRoomClick={handleFreeRoomClick}
                                            onEditClick={handleEditClick}
                                            onBulkCancelClick={handleBulkCancelClick}
                                            date={requiredDateState}
                                            refreshingSlot={refreshingSlotState}
                                        />
                                    </div>
                                )}

                                {/* Table for rooms without a sub group */}
                                {hasRooms && hasNoSubGroupRooms && (
                                    <div className='bookingsTableCard'>
                                        <DailyBookingsTable
                                            lessonStructure={lessonStructureState}
                                            roomsData={roomsBySubGroup.noSubGroup}
                                            cancelBookingFunction={bookingFunction}
                                            onFreeRoomClick={handleFreeRoomClick}
                                            onEditClick={handleEditClick}
                                            onBulkCancelClick={handleBulkCancelClick}
                                            date={requiredDateState}
                                            refreshingSlot={refreshingSlotState}
                                        />
                                    </div>
                                )}

                                {hasRooms && Object.entries(roomsBySubGroup)
                                    .filter(([id]) => id !== 'noSubGroup')
                                    .map(([subId, rooms]) => {
                                        const name = subGroupMeta.find((sg) => sg.groupId === subId)?.name || 'Unknown Sub Group';
                                        return (
                                            <div key={subId}>
                                                <h2 className='bookingsSubGroupHeader'>{name}</h2>
                                                <div className='bookingsTableCard'>
                                                    <DailyBookingsTable
                                                        lessonStructure={lessonStructureState}
                                                        roomsData={rooms}
                                                        cancelBookingFunction={bookingFunction}
                                                        onFreeRoomClick={handleFreeRoomClick}
                                                        onEditClick={handleEditClick}
                                                        onBulkCancelClick={handleBulkCancelClick}
                                                        date={requiredDateState}
                                                        refreshingSlot={refreshingSlotState}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                            </>
                        )}
                    </>
                )}


                {viewModeState === 'week' && (
                    <>
                        <div className='bookingsControlCard'>
                            <WeekPickerComp date={requiredWeekStartPickerState} onWeekChange={onWeekSelectorChange} />
                        </div>

                        {!dateChangeIsLoadedState ? (
                            <LoadingComp />
                        ) : (
                            <>
                                {!hasRooms && (
                                    <div className='bookingsTableCard'>
                                        <DailyBookingsTable
                                            lessonStructure={lessonStructureState}
                                            roomsData={{}}
                                            cancelBookingFunction={bookingFunction}
                                            onFreeRoomClick={handleFreeRoomClick}
                                            onEditClick={handleEditClick}
                                            date={requiredDateState}
                                            refreshingSlot={refreshingSlotState}
                                        />
                                    </div>
                                )}

                                {hasRooms && hasNoSubGroupRooms &&
                                    Object.entries(roomsBySubGroup.noSubGroup).map(([roomId, room]) => (
                                        <div key={roomId}>
                                            <h3 className='bookingsRoomHeader'>{room.name}</h3>
                                            <div className='bookingsTableCard'>
                                                <WeeklyBookingsTable
                                                    lessonStructure={lessonStructureState}
                                                    roomData={room}
                                                    weekDates={weekDatesState}
                                                    cancelBookingFunction={bookingFunction}
                                                    onFreeRoomClick={handleFreeRoomClick}
                                                    onEditClick={handleEditClick}
                                                    onBulkCancelClick={handleBulkCancelClick}
                                                    refreshingSlot={refreshingSlotState}
                                                />
                                            </div>
                                        </div>
                                    ))}

                                {hasRooms && Object.entries(roomsBySubGroup)
                                    .filter(([id]) => id !== 'noSubGroup')
                                    .map(([subId, rooms]) => {
                                        const name = subGroupMeta.find((sg) => sg.groupId === subId)?.name || 'Unknown Sub Group';
                                        return (
                                            <div key={subId}>
                                                <h2 className='bookingsSubGroupHeader'>{name}</h2>
                                                {Object.entries(rooms).map(([roomId, room]) => (
                                                    <div key={roomId}>
                                                        <h3 className='bookingsRoomHeader'>{room.name}</h3>
                                                        <div className='bookingsTableCard'>
                                                            <WeeklyBookingsTable
                                                                lessonStructure={lessonStructureState}
                                                                roomData={room}
                                                                weekDates={weekDatesState}
                                                                cancelBookingFunction={bookingFunction}
                                                                onFreeRoomClick={handleFreeRoomClick}
                                                                onEditClick={handleEditClick}
                                                                onBulkCancelClick={handleBulkCancelClick}
                                                                refreshingSlot={refreshingSlotState}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })}
                            </>
                        )}
                    </>
                )}


            </div>
        )
    }
};
